// SurveyAnalytics/SurveyUploadForm.js
import React, { useEffect, useState } from "react";
import { Modal, Upload, Input, Select, message, Radio } from "antd";
import { FileFilled } from "@ant-design/icons";
import useAxiosInstance from "../../../lib/useAxiosInstance";

const { TextArea } = Input;
const { Dragger } = Upload;

const SurveyUploadForm = ({ isOpen, onClose, onCreated }) => {
  const axiosInstance = useAxiosInstance();

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const [file, setFile] = useState(null);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [surveyName, setSurveyName] = useState("");
  const [surveyType, setSurveyType] = useState("assembly");
  const [surveyDescription, setSurveyDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch clients when modal opens
  useEffect(() => {
    if (!isOpen) return;
    fetchClients();
  }, [isOpen]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);

      // same endpoint you were using earlier; adjust if needed
      const res = await axiosInstance.get("api/clients/getAllClients?status=active");

      // if you still need to filter only active:
      const allClients = res.data?.data || [];
      const activeClients = allClients.filter(
        (client) => client.status === "active"
      );

      setClients(activeClients);
    } catch (error) {
      console.error(error);
      message.error("Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const handleUploadChange = (info) => {
    if (info.fileList.length === 0) {
      setFile(null);
      return;
    }

    const latestFile = info.fileList[info.fileList.length - 1];
    setFile(latestFile.originFileObj || latestFile);
  };

  const resetForm = () => {
    setFile(null);
    setSelectedClientIds([]);
    setSurveyName("");
    setSurveyDescription("");
    setSurveyType("assembly");
  };

  const handleCancel = () => {
    resetForm();
    onClose && onClose();
  };

const handleSubmit = async () => {
  if (!file) {
    message.error("Please upload a file");
    return;
  }

  if (!surveyName.trim()) {
    message.error("Survey Name is required");
    return;
  }

  if (!surveyDescription.trim()) {
    message.error("Survey Description is required");
    return;
  }

  if (selectedClientIds.length === 0) {
    message.error("Please select at least one client");
    return;
  }

  try {
    setSubmitting(true);

    const fileFormData = new FormData();
    fileFormData.append("csvFile", file);             
    fileFormData.append("surveyName", surveyName);
    fileFormData.append("surveyType", surveyType);
    fileFormData.append("surveyDescription", surveyDescription);
    fileFormData.append("clients", JSON.stringify(selectedClientIds));

    const uploadRes = await axiosInstance.post(
      "api/surveyAnalytics/uploadSurveyExcel",
      fileFormData,
      {
        headers: { "Content-Type": "multipart/form-data" }
      }
    );

    // const uploadUrl =
    //   surveyType === "state"
    //     ? "api/surveyAnalytics/uploadSurveyExcelState"
    //     : "api/surveyAnalytics/uploadSurveyExcel";

    // const uploadRes = await axiosInstance.post(
    //   uploadUrl,
    //   fileFormData,
    //   {
    //     headers: { "Content-Type": "multipart/form-data" }
    //   }
    // );

    message.success(uploadRes.data?.message || "Survey uploaded");

    onCreated && onCreated();
    resetForm();
    onClose && onClose();
  } catch (error) {
    console.error(error);
    const backendMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error?.message ||
      "Something went wrong while uploading survey";

    message.error(backendMessage);
  } finally {
    setSubmitting(false);
  }
};


  return (
    <Modal
      className="share-report-modal edit-reports"
      open={isOpen}
      onCancel={handleCancel}
      width={800}
      maskClosable={false}
      okText="Save"
      onOk={handleSubmit}
      confirmLoading={submitting}
    >
      <div className="modal-body-container">
        <div className="modal-title">
          <h3>Upload Survey</h3>
        </div>

        <div className="modal-wrapper-content">
          {/* File upload */}
          <h4 className="pb-4">Upload File</h4>
          <p className="pb-4">
            Click here to upload or drag and drop your file.
          </p>

          <Dragger
            name="file"
            multiple={false}
            beforeUpload={() => false} // prevent auto-upload
            onChange={handleUploadChange}
            accept=".jpg,.jpeg,.png,.pdf,.doc,.xls"
          >
            <p className="ant-upload-drag-icon">
              <FileFilled />
            </p>
            <p className="ant-upload-text">
              Click here to upload or drag drop
              <br />
              (Maximum file size is 50MB)
            </p>
          </Dragger>

          {/* Survey details */}
          <h4 className="py-4">Survey Details</h4>

          <div className="add-modal-row">
            <label>Client</label>
            <Select
              className="custom-dropdown"
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Select client(s)"
              name="clients"
              showSearch
              value={selectedClientIds}
              onChange={setSelectedClientIds}
              loading={loadingClients}
              filterOption={(input, option) =>
                option?.children
                  ?.toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {clients.map((client) => (
                <Select.Option key={client._id} value={client._id}>
                  {`${client.adminFirstName} ${client.adminLastName}`}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="add-modal-row">
            <label>Survey Type</label>
            <Radio.Group
              value={surveyType}
              onChange={(e) => setSurveyType(e.target.value)}
              buttonStyle="solid"
              className="!w-full"
            >
              <Radio.Button value="assembly" className="w-full">Assembly</Radio.Button>
              <Radio.Button value="parliamentary" className="!w-[8rem] ml-4">Parliamentary</Radio.Button>
              <Radio.Button value="state" className="!w-[8rem] ml-4">State</Radio.Button>
            </Radio.Group>
          </div>

          <div className="add-modal-row">
            <label>Survey Name</label>
            <Input
              type="text"
              placeholder="Type Survey Name"
              value={surveyName}
              onChange={(e) => setSurveyName(e.target.value)}
            />
          </div>

          <div className="add-modal-row">
            <label>Survey Description</label>
            <TextArea
              rows={3}
              placeholder="Write Survey Description"
              value={surveyDescription}
              onChange={(e) => setSurveyDescription(e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SurveyUploadForm;
